/**
 * LLM Service for frontend API calls
 * Handles communication with the LLM backend endpoints
 */

import { API_URL } from '../config';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
});

class LLMService {
  /**
   * Get available LLM providers and their capabilities
   */
  async getProviders() {
    try {
      
      
      const response = await fetch(`${API_URL}/api/llm/providers/`, {
        headers: getAuthHeaders()
      });
      
      
      
      if (!response.ok) {
        const errorText = await response.text();
        
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error fetching LLM providers:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch providers'
      };
    }
  }

  /**
   * Generate text using LLM
   * @param {Object} params - Generation parameters
   * @param {string} params.prompt - The main prompt text
   * @param {string} [params.provider] - Provider name (defaults to OpenAI)
   * @param {number[]} [params.contextContentIds] - List of Content IDs for context
   * @param {Object} [params.parameters] - Provider-specific parameters
   * @param {number} [params.conversationId] - Optional conversation ID
   */
  async generateText(params) {
    try {
      const payload = {
        prompt: params.prompt,
        ...(params.provider && { provider: params.provider }),
        ...(params.contextContentIds && { context_content_ids: params.contextContentIds }),
        ...(params.parameters && { parameters: params.parameters }),
        ...(params.conversationId && { conversation_id: params.conversationId })
      };

      const response = await fetch(`${API_URL}/api/llm/generate/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to generate text',
          errorType: data.error_type || 'UnknownError'
        };
      }

      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error generating text:', error);
      return {
        success: false,
        error: error.message || 'Failed to generate text',
        errorType: 'UnknownError'
      };
    }
  }

  /**
   * Enhance existing content using LLM
   * @param {Object} params - Enhancement parameters
   * @param {number} params.contentId - ID of content to enhance
   * @param {string} params.enhancementType - Type of enhancement
   * @param {string} [params.provider] - Provider name
   * @param {Object} [params.parameters] - Provider-specific parameters
   */
  async enhanceContent(params) {
    try {
      const payload = {
        content_id: params.contentId,
        enhancement_type: params.enhancementType,
        ...(params.provider && { provider: params.provider }),
        ...(params.parameters && { parameters: params.parameters })
      };

      const response = await fetch(`${API_URL}/api/llm/enhance/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to enhance content',
          errorType: data.error_type || 'UnknownError'
        };
      }

      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error enhancing content:', error);
      return {
        success: false,
        error: error.message || 'Failed to enhance content',
        errorType: 'UnknownError'
      };
    }
  }

  /**
   * Get all conversations for the current user
   */
  async getConversations() {
    try {
      const response = await fetch(`${API_URL}/api/llm/conversations/`, {
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to fetch conversations'
        };
      }

      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch conversations'
      };
    }
  }

  /**
   * Create a new conversation
   * @param {string} [title] - Optional conversation title
   */
  async createConversation(title = '') {
    try {
      const response = await fetch(`${API_URL}/api/llm/conversations/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to create conversation'
        };
      }

      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error creating conversation:', error);
      return {
        success: false,
        error: error.message || 'Failed to create conversation'
      };
    }
  }

  /**
   * Get conversation history
   * @param {number} conversationId - ID of the conversation
   */
  async getConversationHistory(conversationId) {
    try {
      const response = await fetch(`${API_URL}/api/llm/conversations/${conversationId}/history/`, {
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to fetch conversation history'
        };
      }

      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error fetching conversation history:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch conversation history'
      };
    }
  }

  /**
   * Add a message to an existing conversation
   * @param {number} conversationId - ID of the conversation
   * @param {Object} params - Message parameters (same as generateText)
   */
  async addMessageToConversation(conversationId, params) {
    try {
      const payload = {
        prompt: params.prompt,
        ...(params.provider && { provider: params.provider }),
        ...(params.contextContentIds && { context_content_ids: params.contextContentIds }),
        ...(params.parameters && { parameters: params.parameters })
      };

      const response = await fetch(`${API_URL}/api/llm/conversations/${conversationId}/add_message/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to add message',
          errorType: data.error_type || 'UnknownError'
        };
      }

      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error adding message to conversation:', error);
      return {
        success: false,
        error: error.message || 'Failed to add message',
        errorType: 'UnknownError'
      };
    }
  }

  /**
   * Delete a conversation
   * @param {number} conversationId - ID of the conversation
   */
  async deleteConversation(conversationId) {
    try {
      const response = await fetch(`${API_URL}/api/llm/conversations/${conversationId}/`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const data = await response.json();
        return {
          success: false,
          error: data.error || 'Failed to delete conversation'
        };
      }

      return {
        success: true
      };
    } catch (error) {
      console.error('Error deleting conversation:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete conversation'
      };
    }
  }

  /**
   * Get LLM requests history
   * @param {Object} [filters] - Optional filters
   * @param {number} [filters.conversationId] - Filter by conversation
   * @param {string} [filters.provider] - Filter by provider
   * @param {number} [filters.page] - Page number
   * @param {number} [filters.pageSize] - Page size
   */
  async getRequests(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.conversationId) {
        params.append('conversation_id', filters.conversationId);
      }
      if (filters.provider) {
        params.append('provider', filters.provider);
      }
      if (filters.page) {
        params.append('page', filters.page);
      }
      if (filters.pageSize) {
        params.append('page_size', filters.pageSize);
      }

      const url = `${API_URL}/api/llm/requests/${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to fetch requests'
        };
      }
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error fetching LLM requests:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch requests'
      };
    }
  }

  /**
   * Format error messages for user display
   * @param {string} errorType - Error type from backend
   * @param {string} errorMessage - Error message from backend
   */
  formatErrorMessage(errorType, errorMessage) {
    const errorType_ = errorType?.toLowerCase() || '';
    
    if (errorType_.includes('ratelimit') || errorType_.includes('rate_limit')) {
      return 'Please wait a moment before making another request. The AI service is currently busy.';
    }
    
    if (errorType_.includes('tokenlimit') || errorType_.includes('token_limit')) {
      return 'Your request is too long. Please try with shorter content or reduce the context.';
    }
    
    if (errorType_.includes('authentication') || errorType_.includes('auth')) {
      return 'AI service authentication failed. Please contact support if this continues.';
    }
    
    if (errorType_.includes('provider') || errorType_.includes('service')) {
      return 'AI service is temporarily unavailable. Please try again in a few moments.';
    }
    
    if (errorMessage?.toLowerCase().includes('timeout')) {
      return 'Request timed out. Please try again with a shorter prompt.';
    }
    
    if (errorMessage?.toLowerCase().includes('quota')) {
      return 'AI service quota exceeded. Please try again later or contact support.';
    }
    
    return errorMessage || 'An unexpected error occurred. Please try again or contact support if the problem persists.';
  }

  /**
   * Get enhancement type options
   */
  getEnhancementTypes() {
    return [
      { value: 'improve', label: 'Improve', description: 'Enhance clarity and readability' },
      { value: 'summarize', label: 'Summarize', description: 'Create a concise summary' },
      { value: 'expand', label: 'Expand', description: 'Add more details and examples' },
      { value: 'rewrite', label: 'Rewrite', description: 'Rewrite in a different style' },
      { value: 'grammar', label: 'Grammar Check', description: 'Fix grammar and spelling errors' }
    ];
  }

  /**
   * Get default parameters for a provider
   * @param {string} provider - Provider name
   */
  getDefaultParameters(provider = 'OpenAI') {
    const defaults = {
      'OpenAI': {
        model: 'gpt-4o-mini',
        temperature: 0.7,
        max_tokens: 1000,
        top_p: 1.0,
        frequency_penalty: 0.0,
        presence_penalty: 0.0
      }
    };
    
    return defaults[provider] || defaults['OpenAI'];
  }
}

// Export singleton instance
const llmService = new LLMService();
export default llmService; 